import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X, ShoppingBag, User, Phone, Package } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { AuthService, CollectionsService } from '../firebase';
import { Collection } from '../types/collection';

const HeaderContainer = styled.header`
  /* Arkaplan her zaman beyaz - kaydirmada veya hover'da degismez */
  background: #ffffff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
`;

const HeaderContent = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  height: var(--header-h);

  @media (max-width: 768px) {
    padding: 0 12px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const LogoImage = styled.img`
  height: 80px;
  width: auto;
  display: block;

  /* Mobilde 80px'lik logo header genisliginin cogunu yiyor ve
     hamburger butonunu ekran disina itiyordu. */
  @media (max-width: 768px) {
    height: 42px;
  }
`;

const Nav = styled.nav<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 30px;

  @media (max-width: 768px) {
    position: fixed;
    top: var(--header-h);
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    flex-direction: column;
    padding: 20px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    /* Menu kapaliyken SADECE transform yeterli degildi: nav header'in
       cocugu oldugu icin beyaz arka planin uzerine ciziliyor ve alt
       baglantilari ("Iletisim", "Siparis Takip") header'a siziyordu.
       Gorunurlugu ve tiklanabilirligi de kapatiyoruz. */
    transform: ${props => (props.$isOpen ? 'translateY(0)' : 'translateY(-110%)')};
    visibility: ${props => (props.$isOpen ? 'visible' : 'hidden')};
    opacity: ${props => (props.$isOpen ? 1 : 0)};
    pointer-events: ${props => (props.$isOpen ? 'auto' : 'none')};
    transition: transform 0.3s ease, opacity 0.25s ease, visibility 0.25s ease;
    max-height: calc(100vh - var(--header-h));
    overflow-y: auto;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: #333;
  font-weight: 500;
  font-size: 16px;
  transition: color 0.3s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    color: #333;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownContent = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  min-width: 800px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 25px 20px;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.3s ease;
  z-index: 10000;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const DropdownItem = styled(Link)`
  display: block;
  width: 100%;
  text-align: left;
  padding: 12px 25px;
  color: #333;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s ease;
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    background: rgba(26, 26, 26, 0.1);
    color: #333;
    padding-left: 30px;
  }
`;

const DropdownTitle = styled.div`
  font-weight: 600;
  color: #333;
  padding: 8px 15px;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 10px;
  border-bottom: 1px solid rgba(26, 26, 26, 0.2);
`;

const DropdownSubItem = styled(Link)`
  display: block;
  width: 100%;
  text-align: left;
  padding: 6px 15px;
  color: #666;
  text-decoration: none;
  font-size: 13px;
  transition: all 0.3s ease;
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    background: rgba(26, 26, 26, 0.05);
    color: #333;
    padding-left: 20px;
  }
`;

const DropdownSection = styled.div`
  flex: 1;
  min-width: 150px;
  margin-bottom: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    gap: 2px;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 6px;
  }
  border-radius: 50%;
  transition: all 0.3s ease;
  color: #333;
  position: relative;

  &:hover {
    background-color: #f5f5f5;
    color: #333;
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  background: #ff4757;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [collections, setCollections] = useState<Collection[]>([]);
  const navigate = useNavigate();
  const { getTotalItems } = useCart();

  useEffect(() => {
    const unsubscribe = AuthService.onAuthStateChanged((user) => {
      setIsLoggedIn(!!user);
    });
    
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    try {
      const result = await CollectionsService.getActiveCollections();
      if (result.success && result.data) {
        setCollections(result.data);
      }
    } catch (error) {
      console.error('Error loading collections:', error);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleDropdownEnter = (dropdownName: string) => {
    setActiveDropdown(dropdownName);
  };

  const handleDropdownLeave = () => {
    setActiveDropdown(null);
  };


  return (
    <HeaderContainer>
      <HeaderContent>
        <LogoContainer>
          <Link to="/">
            <LogoImage src="/sarayli.png" alt="Saraylı Moda Logo" />
          </Link>
        </LogoContainer>
        
        <Nav $isOpen={isMenuOpen}>
          <NavLink to="/">Ana Sayfa</NavLink>

          <DropdownContainer
            onMouseEnter={() => handleDropdownEnter('koleksiyonlar')}
            onMouseLeave={handleDropdownLeave}
          >
            <NavLink to="/koleksiyonlar">Koleksiyonlar</NavLink>
            <DropdownContent $isOpen={activeDropdown === 'koleksiyonlar'}>
              <DropdownTitle>Koleksiyonlarımız</DropdownTitle>
              {collections.length > 0 ? (
                collections.map((collection) => (
                  <DropdownItem key={collection.id} to={`/koleksiyonlar/${collection.id}`}>
                    {collection.title}
                  </DropdownItem>
                ))
              ) : (
                <DropdownItem to="/koleksiyonlar">Koleksiyonlar</DropdownItem>
              )}
            </DropdownContent>
          </DropdownContainer>

          <DropdownContainer
            onMouseEnter={() => handleDropdownEnter('urunlerimiz')}
            onMouseLeave={handleDropdownLeave}
          >
            <NavLink to="/ürünlerimiz">Ürünlerimiz</NavLink>
            <DropdownContent $isOpen={activeDropdown === 'urunlerimiz'}>
              <DropdownSection>
                <DropdownTitle>Gelinlikler</DropdownTitle>
                <DropdownSubItem to="/gelinlikler">A Kesim Gelinlikler</DropdownSubItem>
                <DropdownSubItem to="/gelinlikler">Balık Kesim Gelinlikler</DropdownSubItem>
                <DropdownSubItem to="/gelinlikler">Prenses Model Gelinlikler</DropdownSubItem>
                <DropdownSubItem to="/gelinlikler">Bohem Gelinlikler</DropdownSubItem>
                <DropdownSubItem to="/gelinlikler">Tesettür Gelinlikler</DropdownSubItem>
                <DropdownSubItem to="/gelinlikler">Mermaid Gelinlikler</DropdownSubItem>
                <DropdownSubItem to="/gelinlikler">Ball Gown Gelinlikler</DropdownSubItem>
              </DropdownSection>

              <DropdownSection>
                <DropdownTitle>Abiyeler</DropdownTitle>
                <DropdownSubItem to="/abiyeler">Klasik Abiyeler</DropdownSubItem>
                <DropdownSubItem to="/abiyeler">Modern Abiyeler</DropdownSubItem>
                <DropdownSubItem to="/abiyeler">Vintage Abiyeler</DropdownSubItem>
                <DropdownSubItem to="/abiyeler">Bohem Abiyeler</DropdownSubItem>
                <DropdownSubItem to="/abiyeler">Tesettür Abiyeler</DropdownSubItem>
              </DropdownSection>

              <DropdownSection>
                <DropdownTitle>Kınalık</DropdownTitle>
                <DropdownSubItem to="/kinaliklar">Klasik Kınalık Elbiseleri</DropdownSubItem>
                <DropdownSubItem to="/kinaliklar">Modern Kınalık Elbiseleri</DropdownSubItem>
                <DropdownSubItem to="/kinaliklar">Tesettür Kınalık Elbiseleri</DropdownSubItem>
                <DropdownSubItem to="/kinaliklar">Bohem Kınalık Elbiseleri</DropdownSubItem>
              </DropdownSection>

              <DropdownSection>
                <DropdownTitle>Aksesuar</DropdownTitle>
                <DropdownSubItem to="/aksesuar">Gelin Taçları</DropdownSubItem>
                <DropdownSubItem to="/aksesuar">Duvaklar</DropdownSubItem>
                <DropdownSubItem to="/aksesuar">Gelin Ayakkabıları</DropdownSubItem>
                <DropdownSubItem to="/aksesuar">Gelin Çantaları</DropdownSubItem>
                <DropdownSubItem to="/aksesuar">Takılar</DropdownSubItem>
              </DropdownSection>
            </DropdownContent>
          </DropdownContainer>

          <NavLink to="/hakkimizda">Hakkımızda</NavLink>
          <NavLink to="/iletisim">İletişim</NavLink>
          <NavLink to="/siparis-takip">
            <Package size={16} style={{ marginRight: '5px', verticalAlign: 'middle' }} />
            Sipariş Takip
          </NavLink>
        </Nav>

        <ActionButtons>
          <ActionButton onClick={() => navigate('/arama')} aria-label="Ara">
            <Search size={20} />
          </ActionButton>
          <ActionButton onClick={() => navigate(isLoggedIn ? '/profil' : '/login')}>
            <User size={20} />
          </ActionButton>
          <ActionButton onClick={() => navigate('/sepet')}>
            <ShoppingBag size={20} />
            {getTotalItems() > 0 && (
              <CartBadge>{getTotalItems()}</CartBadge>
            )}
          </ActionButton>
          <ActionButton onClick={() => navigate('/randevu')}>
            <Phone size={20} />
          </ActionButton>
          <MobileMenuButton onClick={toggleMenu}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </MobileMenuButton>
        </ActionButtons>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;